import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getNews } from "@/lib/api";
import { Calendar, ArrowRight, Newspaper } from "lucide-react";

export default function NewsSection() {
  const [news, setNews] = useState([]);
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    getNews().then((res) => setNews(res.data)).catch(() => {});
  }, []);

  return (
    <section id="news" data-testid="news-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#F49C03] mb-4">
            Actualités & Innovations
          </p>
          <h2
            className="font-['Quicksand'] text-3xl lg:text-4xl font-medium tracking-tight text-slate-800"
            data-testid="news-title"
          >
            Dernières nouvelles
          </h2>
        </motion.div>

        {news.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Newspaper className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg">Les actualités seront ajoutées via le panneau d'administration.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-[#FAFAFA] rounded-xl overflow-hidden border border-slate-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                data-testid={`news-card-${i}`}
              >
                {item.image_url && (
                  <div className="h-44 overflow-hidden relative">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-lg bg-[#F49C03] text-white text-xs font-bold shadow-sm">
                      Nouveau
                    </div>
                  </div>
                )}
                <div className="p-5">
                  {item.created_at && (
                    <span className="flex items-center text-xs text-slate-400 gap-1 mb-3">
                      <Calendar className="w-3 h-3" />
                      {new Date(item.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  )}
                  <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mb-2 line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-2">
                    {item.summary}
                  </p>
                  <button className="inline-flex items-center gap-2 text-sm font-semibold text-[#F49C03] hover:gap-3 transition-all">
                    Lire la suite <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
